/**
 * useNoticeData Hook
 * 공지사항 관리 데이터 훅
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { format } from 'date-fns';
import { noticeApi } from '@/features/notice/api/noticeApi';
import {
    CreateNoticeRequestDto,
    Notice,
    noticeListFromDto,
    TargetType,
    UpdateNoticeRequestDto,
} from '@/features/notice/types/notice.types';
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";

/** 공지사항 필터 타입 */
export interface NoticeFilter {
    startDate: string;
    endDate: string;
    targetType: TargetType | '';
}

/**
 * 공지사항 관리 데이터 훅
 */
export const useNoticeData = () => {
    const { showToast } = useToast();
    const [allNotices, setAllNotices] = useState<Notice[]>([]);
    const [loading, setLoading] = useState(false);

    // 필터 상태
    const [filter, setFilter] = useState<NoticeFilter>({
        startDate: '',
        endDate: '',
        targetType: '',
    });

    /**
     * 필터 변경 핸들러
     */
    const handleFilterChange = useCallback((updates: Partial<NoticeFilter>) => {
        setFilter(prev => ({ ...prev, ...updates }));
    }, []);

    /**
     * 필터 초기화
     */
    const handleReset = useCallback(() => {
        setFilter({
            startDate: '',
            endDate: '',
            targetType: '',
        });
    }, []);

    /**
     * 필터링된 공지사항 목록
     */
    const notices = useMemo(() => {
        return allNotices.filter(notice => {
            // 게시기간 필터 - 로컬 날짜 문자열로 비교하여 타임존 문제 방지
            if (notice.startDate) {
                const noticeStartStr = format(notice.startDate, 'yyyy-MM-dd');

                // 필터 시작일보다 이전인 공지는 제외
                if (filter.startDate && noticeStartStr < filter.startDate) {
                    return false;
                }

                // 필터 종료일보다 이후인 공지는 제외
                if (filter.endDate && noticeStartStr > filter.endDate) {
                    return false;
                }
            }

            // 대상 타입 필터
            if (filter.targetType && notice.targetType !== filter.targetType) {
                return false;
            }

            return true;
        });
    }, [allNotices, filter]);

    /**
     * 공지사항 목록 조회
     */
    const fetchNotices = useCallback(async () => {
        try {
            setLoading(true);
            const response = await noticeApi.getNoticeList();
            const list = noticeListFromDto(response);
            setAllNotices(list);
        } catch {
            showToast('공지사항 목록을 불러오는데 실패했습니다.', { type: ToastType.ERROR });
            setAllNotices([]);
        } finally {
            setLoading(false);
        }
    }, [showToast]);

    /**
     * 공지사항 등록
     */
    const createNotice = useCallback(async (request: CreateNoticeRequestDto): Promise<boolean> => {
        try {
            setLoading(true);
            const response = await noticeApi.createNotice(request);
            if (response.success) {
                showToast('공지사항이 등록되었습니다.', { type: ToastType.SUCCESS });
                await fetchNotices();
                return true;
            } else {
                showToast(response.message || '공지사항 등록에 실패했습니다.', { type: ToastType.ERROR });
                return false;
            }
        } catch {
            showToast('공지사항 등록에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchNotices]);

    /**
     * 공지사항 수정
     */
    const updateNotice = useCallback(async (request: UpdateNoticeRequestDto): Promise<boolean> => {
        try {
            setLoading(true);
            const response = await noticeApi.updateNotice(request);
            if (response.success) {
                showToast('공지사항이 수정되었습니다.', { type: ToastType.SUCCESS });
                await fetchNotices();
                return true;
            } else {
                showToast(response.message || '공지사항 수정에 실패했습니다.', { type: ToastType.ERROR });
                return false;
            }
        } catch {
            showToast('공지사항 수정에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchNotices]);

    /**
     * 공지사항 삭제
     */
    const deleteNotice = useCallback(async (noticeId: number): Promise<boolean> => {
        try {
            setLoading(true);
            const response = await noticeApi.deleteNotice(noticeId);
            if (response.success) {
                showToast('공지사항이 삭제되었습니다.', { type: ToastType.SUCCESS });
                await fetchNotices();
                return true;
            } else {
                showToast(response.message || '공지사항 삭제에 실패했습니다.', { type: ToastType.ERROR });
                return false;
            }
        } catch {
            showToast('공지사항 삭제에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        } finally {
            setLoading(false);
        }
    }, [showToast, fetchNotices]);

    // 초기 데이터 로드
    useEffect(() => {
        fetchNotices();
    }, [fetchNotices]);

    return {
        notices,
        allNotices,
        loading,
        filter,
        handleFilterChange,
        handleReset,
        fetchNotices,
        createNotice,
        updateNotice,
        deleteNotice
    };
};
